import * as Sentry from '@sentry/react';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const NODE_ENV = import.meta.env.MODE || 'development';

// Determine environment based on deployment context
// Check for Vercel-specific environment variables first
let SENTRY_ENVIRONMENT = 'development';

// Detect preview environments in Vercel
if (import.meta.env.VITE_VERCEL_ENV === 'preview' || 
    window.location.hostname.includes('vercel.app')) {
  SENTRY_ENVIRONMENT = 'preview';
} else if (import.meta.env.VITE_VERCEL_ENV === 'production' || 
           NODE_ENV === 'production') {
  SENTRY_ENVIRONMENT = 'production';
}

// Allow explicit override via environment variable
if (import.meta.env.VITE_SENTRY_ENVIRONMENT) {
  SENTRY_ENVIRONMENT = import.meta.env.VITE_SENTRY_ENVIRONMENT;
}

export function initSentry() {
  if (!SENTRY_DSN) {
    console.warn('Sentry DSN not provided, skipping Sentry initialization');
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    release: import.meta.env.VITE_SENTRY_RELEASE || 'waitlist@1.0.0',
    integrations: [
      new Sentry.BrowserTracing({
        // Set sampling rate for performance monitoring
        // Include vercel.app domains for preview deployments
        tracePropagationTargets: ['localhost', /^https:\/\/.*\.vercel\.app/, /^\/api/],
      }),
      new Sentry.Replay(),
    ],
    environment: SENTRY_ENVIRONMENT,
    // Capture 10% of all transactions for performance monitoring
    tracesSampleRate: NODE_ENV === 'production' ? 0.1 : 0.5,
    // Session replay configuration
    replaysSessionSampleRate: NODE_ENV === 'production' ? 0.1 : 0.5,
    // Error sampling - higher for errors with replay
    replaysOnErrorSampleRate: 1.0,
    // We recommend adjusting this value in production
    normalizeDepth: 10,
    // Enable Sentry debug mode when not in production to help with troubleshooting
    debug: NODE_ENV !== 'production',
    
    // Ensure source maps work correctly with original source URLs
    attachStacktrace: true,
  });
}

// Helper for capturing exceptions
export const captureException = (error: unknown, context?: Record<string, any>) => {
  if (error instanceof Error) {
    Sentry.captureException(error, { 
      extra: context,
    });
  } else {
    Sentry.captureMessage(
      typeof error === 'string' ? error : 'Unknown error occurred',
      { 
        level: 'error',
        extra: context,
      }
    );
  }
};

// Helper for setting user context
export const setUserContext = (email?: string | null) => {
  if (email) {
    Sentry.setUser({ email });
  } else {
    Sentry.setUser(null);
  }
};
